import React, { useState } from "react";
import {
  Pressable,
  StyleSheet,
  View,
  ActivityIndicator,
  type PressableStateCallbackType,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { colors, radii, spacing, webTransitions } from "../../../styles/design-system";
import CustomText from "../../common/general/CustomText/CustomText";

export type AxioButtonVariant = "primary" | "secondary" | "ghost" | "destructive" | "tinted" | "pillOutline";
export type AxioButtonSize = "sm" | "md" | "lg";

export interface AxioButtonProps {
  label: string;
  variant?: AxioButtonVariant;
  size?: AxioButtonSize;
  disabled?: boolean;
  onPress?: () => void;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  accessibilityLabel?: string;
  isLoading?: boolean;
  useTranslationText?: boolean;
}

interface VariantStyles {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  hoverBackgroundColor: string;
  pressedBackgroundColor: string;
  disabledBackgroundColor: string;
  disabledTextColor: string;
}

const getVariantStyles = (variant: AxioButtonVariant): VariantStyles => {
  switch (variant) {
    case "secondary":
      return {
        backgroundColor: colors.white,
        borderColor: colors.grey[300],
        textColor: colors.grey[700],
        hoverBackgroundColor: colors.grey[50],
        pressedBackgroundColor: colors.grey[100],
        disabledBackgroundColor: colors.grey[50],
        disabledTextColor: colors.grey[300],
      };
    case "ghost":
      return {
        // Same as secondary but without the visible border
        backgroundColor: colors.white,
        borderColor: "transparent",
        textColor: colors.grey[700],
        hoverBackgroundColor: colors.grey[50],
        pressedBackgroundColor: colors.grey[100],
        disabledBackgroundColor: colors.grey[50],
        disabledTextColor: colors.grey[300],
      };
    case "destructive":
      return {
        backgroundColor: colors.error[500],
        borderColor: colors.error[500],
        textColor: colors.white,
        hoverBackgroundColor: colors.error[600],
        pressedBackgroundColor: colors.error[700],
        disabledBackgroundColor: colors.error[100],
        disabledTextColor: colors.error[300],
      };
    case "tinted":
      return {
        backgroundColor: colors.brand[50],
        borderColor: colors.brand[200],
        textColor: colors.brand[700],
        hoverBackgroundColor: colors.brand[100],
        pressedBackgroundColor: colors.brand[200],
        disabledBackgroundColor: colors.grey[50],
        disabledTextColor: colors.grey[300],
      };
    case "pillOutline":
      return {
        backgroundColor: colors.white,
        borderColor: "transparent",
        textColor: colors.brand[700],
        hoverBackgroundColor: "transparent",
        pressedBackgroundColor: colors.brand[50],
        disabledBackgroundColor: colors.grey[50],
        disabledTextColor: colors.grey[300],
      };
    case "primary":
    default:
      return {
        backgroundColor: colors.brand[500],
        borderColor: colors.brand[500],
        textColor: colors.white,
        hoverBackgroundColor: colors.brand[600],
        pressedBackgroundColor: colors.brand[700],
        disabledBackgroundColor: colors.brand[100],
        disabledTextColor: colors.brand[300],
      };
  }
};

const AxioButton: React.FC<AxioButtonProps> = ({
  label,
  variant = "primary",
  size = "md",
  disabled = false,
  onPress,
  leftIcon,
  rightIcon,
  style,
  textStyle,
  accessibilityLabel,
  isLoading = false,
  useTranslationText = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const variantStyles = getVariantStyles(variant);

  const isButtonDisabled = disabled || isLoading;

  const handlePress = () => {
    if (!isButtonDisabled && onPress) {
      onPress();
    }
  };

  const buttonStyle = (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
    const { pressed } = state;

    const backgroundColor = isButtonDisabled
      ? variantStyles.disabledBackgroundColor
      : pressed
        ? variantStyles.pressedBackgroundColor
        : isHovered
          ? variantStyles.hoverBackgroundColor
          : variantStyles.backgroundColor;

    const scale = variant === "pillOutline" ? 1 : pressed ? 0.985 : isHovered ? 1.015 : 1;

    const baseStyles = [
      styles.base,
      size === "sm" ? styles.sm : size === "lg" ? styles.lg : styles.md,
      {
        backgroundColor,
        borderColor: variantStyles.borderColor,
        opacity: isButtonDisabled ? 0.7 : 1,
        transform: [{ scale }],
      } as ViewStyle,
      style,
    ];

    return baseStyles;
  };

  const resolvedTextColor = isButtonDisabled ? variantStyles.disabledTextColor : variantStyles.textColor;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      disabled={isButtonDisabled}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={buttonStyle}>
      <View style={styles.content}>
        {isLoading ? (
          <ActivityIndicator
            color={resolvedTextColor}
            style={styles.loader}
            accessibilityLabel={`${accessibilityLabel ?? label} - Loading`}
          />
        ) : (
          <>
            {leftIcon ? <View style={styles.iconLeft}>{leftIcon}</View> : null}
            <CustomText
              style={[
                styles.textBase,
                size === "sm" ? styles.text_sm : size === "lg" ? styles.text_lg : styles.text_md,
                { color: resolvedTextColor },
                textStyle,
              ]}
              text={label}
              useTranslationText={useTranslationText}
            />
            {rightIcon ? <View style={styles.iconRight}>{rightIcon}</View> : null}
          </>
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  base: {
    borderWidth: 1,
    borderRadius: radii.sm,
    justifyContent: "center",
    ...webTransitions.fast,
  },
  sm: {
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  md: {
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  lg: {
    paddingVertical: 10,
    paddingHorizontal: 18,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: spacing.xxs,
    fontFamily: "Inter",
  },
  iconLeft: {
    marginRight: spacing.xxs,
  },
  iconRight: {
    marginLeft: spacing.xxs,
  },
  textBase: {
    fontWeight: "600",
    fontFamily: "Inter",
  },
  text_sm: {
    fontSize: 12,
  },
  text_md: {
    fontSize: 14,
  },
  text_lg: {
    fontSize: 16,
  },
  loader: {
    transform: [{ scale: 0.8 }],
  },
});

export default AxioButton;
